import React, { useEffect, useState } from "react"

import '../design/styles/App.css'
import config from '../config.json'
import { clearSession } from '../auth/session'
import Header from "./Header"
import Nav from './Nav'
import Footer from "./Footer"
import Modules from './Modules'
import Alert from '../components/alert'
import ErrorBoundary from '../components/ErrorBoundary'
import ChangePasswordModal from './ChangePasswordModal'
import PasskeysModal from './PasskeysModal'
import { useBrowserBackButton } from '../hooks/useBrowserBackButton'
import { isPushNotificationsEnabled, registerPushNotifications, setupPushNotificationSync } from '../setup/pushNotifications'
import AiChat from '../ai/AiChat'
import { AiScreenProvider } from '../ai/AiScreenContext'
import { AiAssistantProvider, useAiAssistant } from '../ai/AiAssistantContext'
import AiAssistantModal from '../ai/AiAssistantModal'

const LayoutContent = ({ setLogin, module, setModule, alert, alertOptions, closeAlert }) => {
  const { enabled, loaded } = useAiAssistant()
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [passkeysOpen, setPasskeysOpen] = useState(false)
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false)
  const [logoUrl, setLogoUrl] = useState(null)

  useEffect(() => {
    const client = config.client
    const pdfBase = config.services?.pdf
    if (!client || !pdfBase) return undefined

    let cancelled = false

    fetch(`${pdfBase}/logos/${encodeURIComponent(client)}.png`)
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return
        setLogoUrl(URL.createObjectURL(blob))
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    return () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl)
    }
  }, [logoUrl])

  const onLogout = async () => {
    try {
      if (config.token && config.apiUrl) {
        await fetch(`${config.apiUrl}/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', token: config.token },
        })
      }
    } catch (_) {
      // Local session is cleared even if the server call fails.
    }

    clearSession({ keepClient: false })
    setLogin(true)
  }

  const onPasswordChanged = () => {
    clearSession({ keepClient: true })
    alert('La contraseña se actualizó correctamente. Ingrese nuevamente con su nueva contraseña.')
    setLogin(true)
  }

  return (
    <div id="layout" className="app notranslate" translate="no">
      <Header
        onLogout={onLogout}
        onOpenChangePassword={() => setChangePasswordOpen(true)}
        onOpenPasskeys={() => setPasskeysOpen(true)}
        onOpenAiAssistant={() => setAiAssistantOpen(true)}
        onOpenSettingsModule={(sub) => setModule(['settings', sub])}
        Alert={alert}
      />
      <Nav setModule={setModule} />
      <main className="layout-main">
        {logoUrl && (
          <div
            className="app-background-logo"
            aria-hidden="true"
            style={{ backgroundImage: `url(${logoUrl})` }}
          />
        )}
        {module && (
          <ErrorBoundary>
            <Modules module={module} Alert={alert} setModule={setModule} />
          </ErrorBoundary>
        )}
      </main>
      {
        changePasswordOpen &&
        <ChangePasswordModal
          Alert={alert}
          onClose={() => setChangePasswordOpen(false)}
          onPasswordChanged={onPasswordChanged}
        />
      }
      {
        passkeysOpen &&
        <PasskeysModal Alert={alert} onClose={() => setPasskeysOpen(false)} />
      }
      {
        aiAssistantOpen &&
        <AiAssistantModal Alert={alert} onClose={() => setAiAssistantOpen(false)} />
      }
      {
        alertOptions &&
        <Alert options={alertOptions} closeAlert={closeAlert} />
      }
      {loaded && enabled && <AiChat module={module} />}
      <Footer />
    </div>
  )
}

const Layout = ({ setLogin }) => {

  const [ module, setModule ] = useBrowserBackButton(null)
  const [ alertOptions, setAlertOptions ] = useState(null)

  useEffect(() => {
    if (!isPushNotificationsEnabled()) return
    setupPushNotificationSync()
    registerPushNotifications()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const mel = params.get('mel')
    if (!mel) return

    const msg = params.get('msg')

    params.delete('mel')
    params.delete('msg')
    const remaining = params.toString()
    const cleanUrl = `${window.location.pathname}${remaining ? `?${remaining}` : ''}${window.location.hash}`
    window.history.replaceState({}, '', cleanUrl)

    setModule(['mel', 'con'])

    if (mel === 'connected') {
      alert({
        msg: 'Cuenta de MercadoLibre conectada correctamente.',
        type: 'success',
        vanish: true,
      })
    } else if (mel === 'error') {
      alert({
        msg: msg || 'No se pudo conectar la cuenta de MercadoLibre.',
        type: 'error',
      })
    }
  }, [setModule])

  const alert = (options) => {
    setAlertOptions(options)
  }

  const closeAlert = () => {
      setAlertOptions(null)
  }

  return (
    <AiAssistantProvider>
    <AiScreenProvider>
      <LayoutContent
        setLogin={setLogin}
        module={module}
        setModule={setModule}
        alert={alert}
        alertOptions={alertOptions}
        closeAlert={closeAlert}
      />
    </AiScreenProvider>
    </AiAssistantProvider>
  )
}

export default Layout